import { AlertTriangle } from "lucide-react";
import React, { useState, useEffect } from "react";
import {
  Modal,
  Stack,
  Select,
  TextInput,
  Textarea,
  Button,
  Text,
  Paper,
} from "@mantine/core";
import { useMediaQuery } from "@mantine/hooks";
import { showNotification } from "@mantine/notifications";
import dayjs from "dayjs";
import { EmployeeShiftRecord } from "../services/model/EmployeeShiftRecord";
import { useCreateScheduleOverrideApiService } from "../services/api/ScheduleOverridesApiService";
import {
  OverrideType,
  ScheduleOverride,
} from "../services/model/ScheduleOverride";

interface OverrideModalProps {
  opened: boolean;
  onClose: () => void;
  employee: EmployeeShiftRecord;
  locationId: string;
}

const TIME_FORMAT_REGEX = /^\d{1,2}:\d{2}$/;

const isValidTime = (time: string): boolean => {
  if (!TIME_FORMAT_REGEX.test(time)) return false;
  const [hours, minutes] = time.split(":").map(Number);
  return hours >= 0 && hours <= 23 && minutes >= 0 && minutes <= 59;
};

const normalizeTime = (time: string): string => {
  const [h, m] = time.split(":");
  return `${h.padStart(2, "0")}:${m.padStart(2, "0")}`;
};

const OverrideModal: React.FC<OverrideModalProps> = ({
  opened,
  onClose,
  employee,
  locationId,
}) => {
  const isPhone = useMediaQuery("(max-width: 639px)");
  const [overrideType, setOverrideType] = useState<string | null>(null);
  const [newTime, setNewTime] = useState("");
  const [reason, setReason] = useState("");
  const createOverride = useCreateScheduleOverrideApiService();

  // Reset form state when modal reopens
  useEffect(() => {
    if (opened) {
      setOverrideType(null);
      setNewTime("");
      setReason("");
    }
  }, [opened]);

  const isTimeValid = isValidTime(newTime);

  const handleSave = (): void => {
    if (!overrideType || !reason.trim() || !isTimeValid) return;

    const normalizedTime = normalizeTime(newTime);

    const payload: Partial<ScheduleOverride> = {
      covering_employee: employee.employee_id,
      shift_location: locationId,
      override_type: OverrideType.EXTRA_STAFFING,
      reason: `${overrideType}: ${normalizedTime} - ${reason}`,
      override_date: dayjs().format("YYYY-MM-DD"),
      shift_start: overrideType === "clock_in" ? normalizedTime : undefined,
      shift_end: overrideType === "clock_out" ? normalizedTime : undefined,
    };

    createOverride.mutate(payload, {
      onSuccess: () => {
        onClose();
      },
      onError: () => {
        showNotification({
          title: "Override failed",
          message: "Could not apply the override. Please try again.",
          color: "red",
        });
      },
    });
  };

  return (
    <Modal
      opened={opened}
      onClose={onClose}
      title="Supervisor Override"
      size={isPhone ? "100%" : "md"}
      closeOnClickOutside={false}
      styles={
        isPhone
          ? {
              modal: { height: "100vh", maxHeight: "100vh", borderRadius: 0 },
            }
          : undefined
      }
    >
      <Stack spacing="md">
        <Paper
          p="sm"
          sx={(theme) => ({
            backgroundColor: "rgba(255, 165, 0, 0.08)",
            border: `1px solid ${theme.colors.orange[3]}`,
          })}
          radius="sm"
        >
          <div className="flex items-center gap-xs">
            <AlertTriangle size={16} color="#FFA500" />
            <Text size="sm" color="orange">
              This override will be logged for audit purposes.
            </Text>
          </div>
        </Paper>

        <Text size="sm">
          Employee:{" "}
          <Text component="span" weight={600}>
            {employee.employee_name}
          </Text>
        </Text>

        <Select
          label="Override type"
          placeholder="Select override type"
          data={[
            { value: "clock_in", label: "Clock In Time" },
            { value: "clock_out", label: "Clock Out Time" },
          ]}
          value={overrideType}
          onChange={setOverrideType}
          size="sm"
        />

        <TextInput
          label="New time"
          placeholder="HH:MM (e.g., 08:30)"
          value={newTime}
          onChange={(e) => setNewTime(e.currentTarget.value)}
          error={
            newTime.length > 0 && !isTimeValid ? "Use HH:MM format" : undefined
          }
          size="sm"
        />

        <Textarea
          label="Reason"
          placeholder="Explain the reason for this override"
          value={reason}
          onChange={(e) => setReason(e.currentTarget.value)}
          required
          minRows={3}
          size="sm"
        />

        <div className="flex flex-col-reverse gap-xs sm:flex-row sm:justify-end sm:gap-sm">
          <Button
            variant="subtle"
            onClick={onClose}
            size="sm"
            className="w-full sm:w-auto"
          >
            Cancel
          </Button>
          <Button
            onClick={handleSave}
            loading={createOverride.isLoading}
            disabled={!overrideType || !reason.trim() || !isTimeValid}
            color="orange"
            size="sm"
            className="w-full sm:w-auto"
          >
            Apply Override
          </Button>
        </div>
      </Stack>
    </Modal>
  );
};

export default OverrideModal;
